// Single source of truth for which files the analyze-order flow accepts.
//
// Natively supported files (PDF + images) go through the existing extraction
// pipeline unchanged. Behind the "any-format-orders" PostHog flag, office
// documents and plain text are also accepted — the backend converts them to
// Markdown with anydoc before extraction, and the verify step shows a local
// Markdown preview instead of the PDF/image viewer.

import i18n from "@/lib/i18n/i18n"

export const ANY_FORMAT_ORDERS_FLAG = "any-format-orders"

const NATIVE_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "gif", "webp", "bmp"]

// Must stay in sync with ANYDOC_EXTENSIONS + TEXT_EXTENSIONS in the
// file_sharing lambda (apis/delivery_order_docs.py).
const CONVERTIBLE_EXTENSIONS = [
  "doc",
  "docx",
  "docm",
  "ppt",
  "pps",
  "pot",
  "pptx",
  "pptm",
  "ppsx",
  "ppsm",
  "xls",
  "xlsx",
  "xlsm",
  "xlsb",
  "odt",
  "ods",
  "odp",
  "rtf",
  "epub",
  "csv",
  "txt",
  "md",
]

export function fileExtension(name: string | undefined): string {
  if (!name) return ""
  // Uploaded URLs can carry a query string (S3 presigned GETs)
  const path = name.split("?")[0]
  const base = path.split("/").pop() ?? ""
  const dot = base.lastIndexOf(".")
  return dot === -1 ? "" : base.substring(dot + 1).toLowerCase()
}

export function isNativeOrderFile(file: File): boolean {
  return (
    file.type.startsWith("image/") ||
    file.type === "application/pdf" ||
    NATIVE_EXTENSIONS.includes(fileExtension(file.name))
  )
}

// MIME types for office documents are unreliable across OSes, so this check
// is extension-based — matching how the backend routes the file.
export function isConvertibleOrderFile(fileName: string | undefined): boolean {
  return CONVERTIBLE_EXTENSIONS.includes(fileExtension(fileName))
}

export function isSupportedOrderFile(
  file: File,
  anyFormatEnabled: boolean,
): boolean {
  if (isNativeOrderFile(file)) return true
  return anyFormatEnabled && isConvertibleOrderFile(file.name)
}

const NATIVE_ACCEPT = "image/*,application/pdf"
const CONVERTIBLE_ACCEPT = CONVERTIBLE_EXTENSIONS.map((e) => `.${e}`).join(",")

export function getOrderFileAccept(anyFormatEnabled: boolean): string {
  return anyFormatEnabled
    ? `${NATIVE_ACCEPT},${CONVERTIBLE_ACCEPT}`
    : NATIVE_ACCEPT
}

export function unsupportedOrderFileMessage(anyFormatEnabled: boolean): string {
  return anyFormatEnabled
    ? i18n.t("orders.fileSupport.unsupportedAnyFormat")
    : i18n.t("orders.fileSupport.unsupported")
}

// Converts an office document to Markdown in the browser for the preview
// pane. The backend does its own conversion for extraction — this is
// display-only. The WASM module (~a few MB) is loaded lazily on first use.
export async function convertOrderFileToMarkdown(
  data: ArrayBuffer,
  fileName: string,
): Promise<string> {
  const extension = fileExtension(fileName)
  const bytes = new Uint8Array(data)
  if (extension === "txt" || extension === "md") {
    return new TextDecoder().decode(bytes)
  }
  const anydoc = await import("@firecrawl/anydoc-wasm")
  await anydoc.default()
  const format = anydoc.formatFromExtension(extension) ?? undefined
  return anydoc.toMarkdownBytes(bytes, format)
}
